import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";

import React, { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import HeadingLayout from "src/components/base/heading-layout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import { EllipsisVerticalIcon, LockClosedIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/20/solid";
import { PrimaryButton } from "src/components/base/button";
import CreateTagDialog from "src/components/dialogs/create-tag";
import { SimpleTag } from "src/api/generated/frontend_internal_v1";
import DeleteTagDialog from "src/components/dialogs/delete-tag";
import { Badge } from "src/components/base/badges/base";
import { ApiInternal } from "src/api/api_internal";
import { useForm } from "@tanstack/react-form";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import Form from "src/components/base/form";
import WS from "src/api/ws";
import TablePagination from "src/components/table-pagination";
import RenameTagDialog from "src/components/dialogs/rename-tag";
import ChangeTagDescDialog from "src/components/dialogs/change-tag-desc";

/**
 * The properties for {@link TagManagement}
 */
export type TagManagementProps = {};

const LIMIT = 25;

/**
 * The management overview for tags
 */
function TagManagement(props: TagManagementProps) {
    const [t] = useTranslation("admin-tag-overview");
    const [tg] = useTranslation();

    const navigate = useNavigate();
    const router = useRouter();

    const { page, search } = Route.useSearch();
    const data = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    useEffect(() => {
        const listener = WS.addEventListener("message.TagsChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    const [openCreateTag, setOpenCreateTag] = React.useState(false);
    const [openRenameTag, setOpenRenameTag] = React.useState<SimpleTag>();
    const [openSetTagDesc, setOpenSetTagDesc] = React.useState<SimpleTag>();
    const [openDeleteTag, setOpenDeleteTag] = React.useState<SimpleTag>();

    return (
        <HeadingLayout
            heading={t("heading.tag-overview")}
            headingDescription={t("description.tags")}
            headingChildren={
                <PrimaryButton onClick={() => setOpenCreateTag(true)}>{t("button.create-tag")}</PrimaryButton>
            }
        >
            <Form className={"grid grid-cols-2 gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"search"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) =>
                                    await navigate({
                                        to: "/a/tags",
                                        search: {
                                            page: 1,
                                            search: fieldApi.state.value,
                                        },
                                    }),
                                onChangeAsyncDebounceMs: 500,
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.filter")}</Label>
                                    <Input
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
            </Form>

            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.tag-name")}</TableHeader>
                        <TableHeader>{t("label.tag-description")}</TableHeader>
                        <TableHeader className={"w-0"}>
                            <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                        </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map((tag) => (
                        <TableRow key={tag.uuid} href={"/a/tags/$tagId/associated"} params={{ tagId: tag.uuid }}>
                            <TableCell>
                                <div className={"flex items-center gap-3"}>
                                    <span>{tag.name}</span>
                                    {tag.locked && (
                                        <Badge color={"orange"}>
                                            <LockClosedIcon className="size-4" />
                                            <span>{t("label.read-only")}</span>
                                        </Badge>
                                    )}
                                </div>
                            </TableCell>
                            <TableCell className={"w-[50ch] text-wrap break-all whitespace-pre-wrap"}>
                                {tag.description}
                            </TableCell>
                            <TableCell>
                                <Dropdown>
                                    <DropdownButton plain={true}>
                                        <EllipsisVerticalIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor={"bottom end"}>
                                        <DropdownSection>
                                            <DropdownItem disabled={tag.locked} onClick={() => setOpenRenameTag(tag)}>
                                                <PencilSquareIcon />
                                                <DropdownLabel>{t("button.rename-tag")}</DropdownLabel>
                                            </DropdownItem>
                                            <DropdownItem disabled={tag.locked} onClick={() => setOpenSetTagDesc(tag)}>
                                                <PencilSquareIcon />
                                                <DropdownLabel>{t("button.set-tag-description")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                        <DropdownSection>
                                            <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>
                                            <DropdownItem disabled={tag.locked} onClick={() => setOpenDeleteTag(tag)}>
                                                <TrashIcon />
                                                <DropdownLabel>{t("button.delete-tag")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                    </DropdownMenu>
                                </Dropdown>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/a/tags"}
                getSearchParams={(newPage: number) => ({ search, page: newPage })}
            />

            {openCreateTag && (
                <Suspense>
                    <CreateTagDialog
                        onClose={() => {
                            setOpenCreateTag(false);
                        }}
                    />
                </Suspense>
            )}

            {openRenameTag && (
                <Suspense>
                    <RenameTagDialog
                        tag={openRenameTag}
                        onClose={() => {
                            setOpenRenameTag(undefined);
                        }}
                        onSubmit={async () => {
                            setOpenRenameTag(undefined);
                            await router.invalidate({ sync: true });
                        }}
                    />
                </Suspense>
            )}

            {openSetTagDesc && (
                <Suspense>
                    <ChangeTagDescDialog
                        tag={openSetTagDesc}
                        onClose={() => {
                            setOpenSetTagDesc(undefined);
                        }}
                        onSubmit={async () => {
                            setOpenSetTagDesc(undefined);
                            await router.invalidate({ sync: true });
                        }}
                    />
                </Suspense>
            )}

            {openDeleteTag && (
                <Suspense>
                    <DeleteTagDialog
                        tag={openDeleteTag}
                        onDeletion={() => setOpenDeleteTag(undefined)}
                        onClose={() => setOpenDeleteTag(undefined)}
                    />
                </Suspense>
            )}
        </HeadingLayout>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type TagsOverviewSearch = {
    /** The search term that should be used */
    search: string;
    /** The current page number we're on */
    page: number;
};

export const Route = createFileRoute("/_admin/a/tags/")({
    component: TagManagement,
    validateSearch: (search: Record<string, unknown>): TagsOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return { page: page <= 0 ? 1 : page, search: (search.search as string) || "" };
    },
    loaderDeps: ({ search: { search, page } }) => ({ page, search }),
    loader: async ({ deps }) =>
        await ApiInternal.tags.getAll({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
            filter_name: deps.search,
        }),
});
